import { create } from 'zustand';

/**
 * Who is showing the offline panel (021 FR-007, FR-022).
 *
 * FR-007 wants exactly ONE statement of the offline condition, and the panel has to appear in two
 * different places to satisfy FR-022: inside the authenticated layout it replaces only the content
 * area, so the toolbar and navigation stay usable; on an anonymous surface there is no layout to
 * sit inside.
 *
 * The first version discriminated on the session's `isSignedIn` — reasoning "signed in ⇒ the
 * layout is on screen". That is FALSE for `/password-reset` and `/verify-email`: both are
 * top-level routes outside the layout, and 018's link-token guard admits a SIGNED-IN practitioner
 * to them deliberately — its own comment calls that the dominant case for verify-email. Offline on
 * either page the root suppressed the panel because the session said "signed in", and the layout
 * never rendered one because the layout was not mounted. FR-007's single indication became NO
 * indication, on exactly the surfaces a link from a mailbox lands on. `/simplify` found it.
 *
 * A CLAIM rather than a guess. The shell knows whether it is on screen; the root cannot know and
 * should not infer it from an unrelated fact. The layout claims while mounted and releases when it
 * unmounts; the root renders the panel only when nothing has claimed it. Correct in all three
 * states rather than two, and it removes the root's dependency on the session entirely.
 *
 * ---
 *
 * 034: an `@Injectable` with a `#private` signal became a MODULE-PRIVATE Zustand store with named
 * writers, the shape this module already used. The privacy argument survives the change
 * intact and matters slightly more here: a Zustand store object exposes `setState`, so exporting it
 * would hand anyone the ability to set the count directly — which is exactly the imbalance the
 * clamp below exists to absorb rather than to invite.
 *
 * The one genuine difference is the CLAIM'S LIFETIME. Angular's layout claimed in its constructor
 * and released through `DestroyRef`; React's claims from an effect and releases from its cleanup,
 * which under `StrictMode` runs mount→unmount→mount in development. That is precisely why the
 * count is a count: the double-invocation is an overlap, and a boolean would end it at `false`.
 */
const store = create<{ claims: number }>()(() => ({ claims: 0 }));

/** True when no shell is showing the panel, so the root must. */
export const isOfflinePanelUnclaimed = (): boolean => store.getState().claims === 0;

/** Read inside a component. Re-renders on change. */
export const useOfflinePanelUnclaimed = (): boolean => store((s) => s.claims === 0);

/**
 * A count rather than a boolean, and DEFENSIVELY rather than because it is needed today.
 *
 * The honest position: the layout is mounted at exactly one route and is the only claimer, and a
 * router deactivates an outgoing subtree before activating the incoming one — so two shells never
 * coexist in production and this never exceeds 1. A boolean would work.
 *
 * It stays a count because the failure a boolean allows is the worse one: if two shells ever did
 * overlap, the outgoing one's release would clear the incoming one's claim and the practitioner
 * would see two offline panels — the exact thing FR-007 forbids. The clamp absorbs an imbalance
 * rather than pretending it cannot happen.
 *
 * Stated as a guard rather than as an observation, because the previous version of this mechanism
 * failed by asserting a premise about the router that was not true. 034 adds a second reason that
 * is not defensive at all: `StrictMode` double-invokes an effect, so the overlap the Angular
 * version could only hypothesise about happens on every mount in development.
 */
export const claimOfflinePanel = (): void => store.setState((s) => ({ claims: s.claims + 1 }));

export const releaseOfflinePanel = (): void =>
  store.setState((s) => ({ claims: Math.max(0, s.claims - 1) }));

/**
 * Test seam, like the other stores'. Production code never calls it.
 *
 * Angular needed none: `TestBed.inject` handed every test a fresh instance. Module state outlives a
 * test, so without this one case's unbalanced claim is what the next case starts from.
 */
export const resetOfflinePresentation = (): void => store.setState({ claims: 0 });
